import { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import "@/App.css";
import { DraftGuardProvider } from "./context/DraftGuardContext";
import lazyWithReload from "./lib/lazyWithReload";
import { LOCALIZED_ROUTES, publicPage } from "./publicRoutes";
import { SitePages } from "./components/public/SiteLang";
import { findPage } from "./lib/seo";

// The signed-in shell (menu bar, settings frame, permission and module gates)
// is one chunk of its own: the public pages never download it. Screens behind
// the login start fetching it at once (below), and every visitor fetches it in
// the background a few seconds after the page settles, ready for the sign-in.
const shell = () => import(/* webpackChunkName: "app-shell" */ "./components/appShell");
const Layout = lazyWithReload(() => shell().then((m) => ({ default: m.Layout })));
const SettingsLayout = lazyWithReload(() => shell().then((m) => ({ default: m.SettingsLayout })));
const RequirePerm = lazyWithReload(() => shell().then((m) => ({ default: m.RequirePerm })));
const RequireFeature = lazyWithReload(() => shell().then((m) => ({ default: m.RequireFeature })));
const preloadShell = () => Promise.all([Layout, SettingsLayout, RequirePerm, RequireFeature].map((c) => c.preload())).catch(() => {});
if (typeof window !== "undefined") {
  if (!findPage(window.location.pathname)) preloadShell();
  else window.addEventListener("load", () => { setTimeout(preloadShell, 3000); }, { once: true });
}
import VisitTracker from "./components/VisitTracker";
import PageMeta from "./components/PageMeta";
import { isPlatformHost } from "./lib/onlineStore";
import { fetchPublicStore } from "./lib/onlineStoreApi";

// Pages are code-split: each becomes its own chunk loaded on demand, which keeps
// heavy deps (recharts, jspdf, html2canvas, …) out of the initial bundle.
const Auth = publicPage("/auth");
const Dashboard = lazyWithReload(() => import("./pages/Dashboard"));
const InvoiceCreate = lazyWithReload(() => import("./pages/InvoiceCreate"));
const InvoiceView = lazyWithReload(() => import("./pages/InvoiceView"));
const Invoices = lazyWithReload(() => import("./pages/Invoices"));
const Estimates = lazyWithReload(() => import("./pages/Estimates"));
const EstimateCreate = lazyWithReload(() => import("./pages/EstimateCreate"));
const EstimateView = lazyWithReload(() => import("./pages/EstimateView"));
const BackupExport = lazyWithReload(() => import("./pages/BackupExport"));
const Clients = lazyWithReload(() => import("./pages/Clients"));
const Products = lazyWithReload(() => import("./pages/Products"));
const Labels = lazyWithReload(() => import("./pages/Labels"));
const Expenses = lazyWithReload(() => import("./pages/Expenses"));
const Payments = lazyWithReload(() => import("./pages/Payments"));
const Taxes = lazyWithReload(() => import("./pages/Taxes"));
const Banks = lazyWithReload(() => import("./pages/Banks"));
const MailPage = lazyWithReload(() => import("./pages/Mail"));
const AdminDashboard = lazyWithReload(() => import("./pages/AdminDashboard"));
const AdminBillingSettings = lazyWithReload(() => import("./pages/AdminBillingSettings"));
const SubscriptionDashboard = lazyWithReload(() => import("./pages/SubscriptionDashboard"));
const BusinessSettings = lazyWithReload(() => import("./pages/settings/BusinessSettings"));
const AccountSettings = lazyWithReload(() => import("./pages/settings/AccountSettings"));
const CustomizationSettings = lazyWithReload(() => import("./pages/settings/CustomizationSettings"));
const TeamSettings = lazyWithReload(() => import("./pages/settings/TeamSettings"));
const TariffsSettings = lazyWithReload(() => import("./pages/settings/TariffsSettings"));
const GestorAccessSettings = lazyWithReload(() => import("./pages/settings/GestorAccessSettings"));
const PaymentsSettings = lazyWithReload(() => import("./pages/settings/PaymentsSettings"));
const ApiSettings = lazyWithReload(() => import("./pages/settings/ApiSettings"));
const GestorWorkspace = lazyWithReload(() => import("./pages/GestorWorkspace"));
const AdminGestores = lazyWithReload(() => import("./pages/AdminGestores"));
const LandingPage = publicPage("/");
const Funcionalidades = publicPage("/funcionalidades");
const SistemaVerifactu = publicPage("/sistema-verifactu");
const Calculadoras = publicPage("/calculadoras");
const CalendarioFiscal = publicPage("/calendario-fiscal");
const Privacy = publicPage("/privacy");
const AvisoLegal = publicPage("/aviso-legal");
const CondicionesLegales = publicPage("/condiciones-legales");
const PoliticaCookies = publicPage("/politica-de-cookies");
const PayInvoice = lazyWithReload(() => import("./pages/PayInvoice"));
const LicenseRenewal = publicPage("/licencia");
const Blog = publicPage("/blog");
const VerifactuAplazamiento2028 = publicPage("/blog/verifactu-aplazamiento-2028");
const AppGratuitaAeat = publicPage("/verifactu/app-gratuita-aeat");
const FacturaElectronicaB2b = publicPage("/factura-electronica-obligatoria-b2b");
const Gestorias = publicPage("/software-facturacion-gestorias");
const ProgramaTpv = publicPage("/programa-facturacion-tpv");
const LectorOcr = publicPage("/lector-ocr-facturas-gastos");
const TiendaOnline = publicPage("/programa-facturacion-tienda-online");
const AlternativaQuipu = publicPage("/alternativa-a-quipu");
const AlternativaHolded = publicPage("/alternativa-a-holded");
const Remittances = lazyWithReload(() => import("./pages/Remittances"));
const Treasury = lazyWithReload(() => import("./pages/Treasury"));
const Reports = lazyWithReload(() => import("./pages/Reports"));
const Orders = lazyWithReload(() => import("./pages/Orders"));
const OrderCreate = lazyWithReload(() => import("./pages/OrderCreate"));
const OrderView = lazyWithReload(() => import("./pages/OrderView"));
const Deliveries = lazyWithReload(() => import("./pages/Deliveries"));
const DeliveryView = lazyWithReload(() => import("./pages/DeliveryView"));
const TrackOrder = lazyWithReload(() => import("./pages/TrackOrder"));
const Stores = lazyWithReload(() => import("./pages/Stores"));
const TPV = lazyWithReload(() => import("./pages/TPV"));
const MaterialsApp = lazyWithReload(() => import("./pages/materials/MaterialsApp"));
const Catalogues = lazyWithReload(() => import("./pages/Catalogues"));
const CatalogueEditor = lazyWithReload(() => import("./pages/CatalogueEditor"));
const PublicCatalogue = lazyWithReload(() => import("./pages/PublicCatalogue"));
const Storefront = lazyWithReload(() => import("./pages/Storefront"));
const ShopOnOwnDomain = lazyWithReload(() => import("./pages/Storefront").then((m) => ({ default: m.ShopOnOwnDomain })));
const OnlineStore = lazyWithReload(() => import("./pages/OnlineStore"));
const BookMeCalendar = lazyWithReload(() => import("./pages/bookme/Calendar"));
const BookMeBookingForm = lazyWithReload(() => import("./pages/bookme/BookingForm"));
const BookMeBookingDetail = lazyWithReload(() => import("./pages/bookme/BookingDetail"));
const BookMeSites = lazyWithReload(() => import("./pages/bookme/Sites"));
const BookMeTags = lazyWithReload(() => import("./pages/bookme/Tags"));
const BookMeSettings = lazyWithReload(() => import("./pages/bookme/Settings"));
const BookMeImport = lazyWithReload(() => import("./pages/bookme/Import"));
const BookMeImportHistory = lazyWithReload(() => import("./pages/bookme/ImportHistory"));
const BookMeImportDetail = lazyWithReload(() => import("./pages/bookme/ImportDetail"));
const BookMeShareLinks = lazyWithReload(() => import("./pages/bookme/ShareLinks"));
const BookMeBillingSchemes = lazyWithReload(() => import("./pages/bookme/BillingSchemes"));
const BookMeBillingCreate = lazyWithReload(() => import("./pages/bookme/BillingCreate"));
const BookMeBillingInvoice = lazyWithReload(() => import("./pages/bookme/BillingInvoice"));
// Public client portal (no Layout, no session): its own lazy chunk.
const BookMePortal = lazyWithReload(() => import("./pages/bookme/PublicPortal"));

// Shown while a route chunk is being fetched.
const PageFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="w-8 h-8 border-2 border-[#1a4f8b] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Force a fresh editor instance per document type / edited id. Without this,
// React Router keeps the same <InvoiceCreate/> mounted when only the ?docType=
// (or ?id=) query changes, so switching factura⇄albarán⇄proforma carries the
// state over and the autosave re-parks the same document under every type's
// draft key — the "one document shows in three sites" bug. Keying by the query
// remounts the editor clean for each document.
function InvoiceCreateRoute() {
  const [sp] = useSearchParams();
  return <InvoiceCreate key={sp.get('id') || sp.get('docType') || 'invoice'} />;
}
// The shop's short address, /<name>/store, lands on the shop itself.
function StoreAlias() {
  const p = useParams();
  return <Navigate to={`/tienda/${p.slug}${p['*'] ? `/${p['*']}` : ''}${window.location.search}`} replace />;
}
function EstimateCreateRoute() {
  const [sp] = useSearchParams();
  return <EstimateCreate key={sp.get('id') || 'new'} />;
}

// A shop on its own domain (Tienda online › Dominio propio): a host that is not
// zBilling's serves that shop at its root. A host no shop has claimed falls
// through to the normal app, so a new platform domain keeps working.
const foreignHost = typeof window !== 'undefined' && !isPlatformHost(window.location.hostname)
  ? window.location.hostname : null;

function App() {
  const [shopHost, setShopHost] = useState(foreignHost ? undefined : null); // undefined = asking
  useEffect(() => {
    if (!foreignHost) return;
    fetchPublicStore({ host: foreignHost }).then((s) => setShopHost(s ? foreignHost : null)).catch(() => setShopHost(null));
  }, []);
  if (shopHost === undefined) return <PageFallback />;
  if (shopHost) {
    return (
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <ShopOnOwnDomain host={shopHost} />
        </Suspense>
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <VisitTracker />
      <PageMeta />
      <DraftGuardProvider>
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/auth" element={<Auth />} />
        
        {/* Public website: every page reads in the language of its address
            (/fr/… French, no prefix Spanish) — components/public/SiteLang */}
        <Route element={<SitePages />}>
        {/* Public Landing Page */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/funcionalidades" element={<Funcionalidades />} />
        <Route path="/sistema-verifactu" element={<SistemaVerifactu />} />
        <Route path="/verifactu" element={<SistemaVerifactu />} />
        <Route path="/calculadoras" element={<Calculadoras />} />
        <Route path="/calendario-fiscal" element={<CalendarioFiscal />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacidad" element={<Privacy />} />
        <Route path="/politica-de-privacidad" element={<Privacy />} />
        <Route path="/aviso-legal" element={<AvisoLegal />} />
        <Route path="/condiciones-legales" element={<CondicionesLegales />} />
        <Route path="/terminos" element={<CondicionesLegales />} />
        <Route path="/politica-de-cookies" element={<PoliticaCookies />} />
        <Route path="/cookies" element={<PoliticaCookies />} />

        {/* Public guides, articles and product pages (src/content/pages) */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/verifactu-aplazamiento-2028" element={<VerifactuAplazamiento2028 />} />
        <Route path="/verifactu/app-gratuita-aeat" element={<AppGratuitaAeat />} />
        <Route path="/factura-electronica-obligatoria-b2b" element={<FacturaElectronicaB2b />} />
        <Route path="/software-facturacion-gestorias" element={<Gestorias />} />
        <Route path="/programa-facturacion-tpv" element={<ProgramaTpv />} />
        <Route path="/lector-ocr-facturas-gastos" element={<LectorOcr />} />
        <Route path="/programa-facturacion-tienda-online" element={<TiendaOnline />} />
        <Route path="/alternativa-a-quipu" element={<AlternativaQuipu />} />
        <Route path="/alternativa-a-holded" element={<AlternativaHolded />} />

        {/* The same pages in the other languages, and /es/… (lib/siteLanguages.js) */}
        {LOCALIZED_ROUTES.map((r) => <Route key={r.path} path={r.path} element={<r.Component />} />)}
        </Route>

        {/* Public customer payment page (no auth) */}
        <Route path="/pay/:token" element={<PayInvoice />} />
        <Route path="/billing/pay/:token" element={<PayInvoice />} />
        <Route path="/book-me/shared/:token" element={<BookMePortal />} />

        {/* Public ZPOS licence renewal by card (no auth): the till opens it with its key */}
        <Route path="/licencia" element={<LicenseRenewal />} />

        {/* Public live delivery tracking (no auth) */}
        <Route path="/track/:token" element={<TrackOrder />} />

        {/* Public product catalogue (no auth) — only published catalogues resolve */}
        <Route path="/catalogue/:slug" element={<PublicCatalogue />} />

        {/* Public web shop of an account (no auth) — only a published shop resolves */}
        <Route path="/tienda/:slug/*" element={<Storefront />} />
        <Route path="/:slug/store/*" element={<StoreAlias />} />
        
        {/* Protected Routes wrapped in Layout */}
        <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
        <Route path="/invoices/new" element={<Layout><RequirePerm anyOf={['billing.create', 'billing.edit']}><InvoiceCreateRoute /></RequirePerm></Layout>} />
        <Route path="/invoices/view/:id" element={<Layout><RequirePerm perm="billing.view"><InvoiceView /></RequirePerm></Layout>} />

        <Route path="/invoices" element={<Layout><RequirePerm perm="billing.view"><Invoices /></RequirePerm></Layout>} />
        <Route path="/estimates" element={<Layout><RequirePerm perm="billing.view"><Estimates /></RequirePerm></Layout>} />
        <Route path="/estimates/new" element={<Layout><RequirePerm anyOf={['billing.create', 'billing.edit']}><EstimateCreateRoute /></RequirePerm></Layout>} />
        <Route path="/estimates/view/:id" element={<Layout><RequirePerm perm="billing.view"><EstimateView /></RequirePerm></Layout>} />
        <Route path="/documents/backup" element={<Layout><RequirePerm perm="billing.view"><BackupExport /></RequirePerm></Layout>} />
        <Route path="/clients" element={<Layout><RequirePerm anyOf={['clients.view', 'contacts.viewCustomers']}><Clients initialTab="Clientes" /></RequirePerm></Layout>} />
        <Route path="/suppliers" element={<Layout><RequirePerm anyOf={['contacts.viewSuppliers', 'clients.view']}><Clients initialTab="Proveedores" /></RequirePerm></Layout>} />
        <Route path="/products" element={<Layout><RequirePerm perm="billing.view"><Products /></RequirePerm></Layout>} />
        <Route path="/products/labels" element={<Layout><RequirePerm perm="billing.view"><Labels /></RequirePerm></Layout>} />
        {/* Catálogos — gated per client by the product_catalogue feature flag (entry point: Products, next to Escanear) */}
        <Route path="/catalogues" element={<Layout><RequireFeature feature="product_catalogue"><RequirePerm perm="catalogue.view"><Catalogues /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/catalogues/new" element={<Layout><RequireFeature feature="product_catalogue"><RequirePerm perm="catalogue.create"><CatalogueEditor /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/catalogues/:id/edit" element={<Layout><RequireFeature feature="product_catalogue"><RequirePerm perm="catalogue.edit"><CatalogueEditor /></RequirePerm></RequireFeature></Layout>} />
        {/* Tienda online — gated per client by the online_store feature flag */}
        <Route path="/online-store" element={<Layout><RequireFeature feature="online_store"><OnlineStore /></RequireFeature></Layout>} />
        {/* Book Me Calendar — gated per client by the book_me feature flag */}
        <Route path="/book-me" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.view"><BookMeCalendar /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/new" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.manage"><BookMeBookingForm /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/b/:id" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.view"><BookMeBookingDetail /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/b/:id/edit" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.manage"><BookMeBookingForm /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/sites" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.manage"><BookMeSites /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/tags" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.settings"><BookMeTags /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/settings" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.settings"><BookMeSettings /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/import" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.import"><BookMeImport /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/imports" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.import"><BookMeImportHistory /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/imports/:id" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.import"><BookMeImportDetail /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/share" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.share"><BookMeShareLinks /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/billing-schemes" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.settings"><BookMeBillingSchemes /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/billing" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.documents"><BookMeBillingCreate /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/book-me/billing/:invoiceId" element={<Layout><RequireFeature feature="book_me"><RequirePerm perm="bookme.view"><BookMeBillingInvoice /></RequirePerm></RequireFeature></Layout>} />
        <Route path="/orders" element={<Layout><RequirePerm anyOf={['orders.view', 'orders.create']}><Orders /></RequirePerm></Layout>} />
        <Route path="/orders/new" element={<Layout><RequirePerm perm="orders.create"><OrderCreate /></RequirePerm></Layout>} />
        <Route path="/orders/view/:id" element={<Layout><RequirePerm anyOf={['orders.view', 'orders.create']}><OrderView /></RequirePerm></Layout>} />
        <Route path="/deliveries" element={<Layout><RequirePerm anyOf={['delivery.view', 'delivery.drive']}><Deliveries /></RequirePerm></Layout>} />
        <Route path="/deliveries/view/:id" element={<Layout><RequirePerm anyOf={['delivery.view', 'delivery.drive']}><DeliveryView /></RequirePerm></Layout>} />
        <Route path="/expenses" element={<Layout><Expenses /></Layout>} />
        <Route path="/taxes" element={<Layout><RequirePerm anyOf={['finance.viewTax', 'finance.reports']}><Taxes /></RequirePerm></Layout>} />
        <Route path="/banks" element={<Layout><RequirePerm anyOf={['finance.viewPayments', 'billing.view']}><Banks /></RequirePerm></Layout>} />
        <Route path="/mail" element={<Layout><MailPage /></Layout>} />
        <Route path="/payments" element={<Layout><RequirePerm anyOf={['finance.viewPayments', 'billing.view']}><Payments /></RequirePerm></Layout>} />
        <Route path="/remittances" element={<Layout><RequirePerm anyOf={['finance.viewPayments', 'billing.view']}><Remittances /></RequirePerm></Layout>} />
        <Route path="/treasury" element={<Layout><RequirePerm anyOf={['finance.reports', 'reports.dashboards']}><Treasury /></RequirePerm></Layout>} />
        <Route path="/reports" element={<Layout><RequirePerm anyOf={['reports.dashboards', 'finance.reports']}><Reports /></RequirePerm></Layout>} />
        <Route path="/stores" element={<Layout><RequireFeature feature="zpos_tiendas"><Stores /></RequireFeature></Layout>} />
        <Route path="/tpv" element={<Layout><RequirePerm anyOf={['billing.create', 'billing.view']}><TPV /></RequirePerm></Layout>} />

        {/* Material Management ERP — gated per client by the material_management feature flag */}
        <Route path="/materials" element={<Layout><RequireFeature feature="material_management"><MaterialsApp /></RequireFeature></Layout>} />
        <Route path="/materials/:moduleKey" element={<Layout><RequireFeature feature="material_management"><MaterialsApp /></RequireFeature></Layout>} />
        <Route path="/admin" element={<Layout><AdminDashboard /></Layout>} />
        <Route path="/admin/billing" element={<Layout><AdminBillingSettings /></Layout>} />
        <Route path="/admin/gestores" element={<Layout><AdminGestores /></Layout>} />
        <Route path="/subscription" element={<Layout><SubscriptionDashboard /></Layout>} />
        
        {/* Settings Routes wrapped in SettingsLayout */}
        <Route path="/settings/business" element={<Layout><SettingsLayout><BusinessSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/account" element={<Layout><SettingsLayout><AccountSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/customization" element={<Layout><SettingsLayout><CustomizationSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/team" element={<Layout><SettingsLayout><TeamSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/tariffs" element={<Layout><SettingsLayout><TariffsSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/gestor" element={<Layout><SettingsLayout><GestorAccessSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/payments" element={<Layout><SettingsLayout><PaymentsSettings /></SettingsLayout></Layout>} />
        <Route path="/settings/api" element={<Layout><SettingsLayout><ApiSettings /></SettingsLayout></Layout>} />

        {/* Gestoría workspace (accountant-role users) */}
        <Route path="/gestor" element={<Layout><GestorWorkspace /></Layout>} />

        <Route path="/settings" element={<Navigate to="/settings/business" replace />} />
      </Routes>
      </Suspense>
      </DraftGuardProvider>
    </BrowserRouter>
  );
}

export default App;
